import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Filter, Tag, Zap } from 'lucide-react';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { cn } from '@/lib/utils';
import type { EditorTab } from './editor-shell';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

const BASIC_FIELDS = new Set([
	'name',
	'code',
	'priority',
	'description',
	'badgeText',
	'badgeImageUrl',
	'backgroundImageUrl',
	'frontLabel',
	'colorHex',
	'backgroundColorHex',
	'externalUrl',
	'isActive',
	'isVisible',
	'hideFromOffers'
]);

interface EditorTabRailProps {
	activeTab: EditorTab;
	onTabChange: (tab: EditorTab) => void;
}

export function EditorTabRail({ activeTab, onTabChange }: EditorTabRailProps) {
	const { t } = useTranslation();
	const {
		control,
		formState: { errors }
	} = useFormContext<PromotionFormValues>();
	const v = useWatch({ control }) as PromotionFormValues;

	const conditionsCount = [
		v.activeFrom || v.activeTo,
		v.minAmount != null || v.maxAmount != null || v.minQuantity != null,
		v.productIds?.length || v.categoryIds?.length || v.manufacturerIds?.length,
		v.customerCategoryIds?.length || v.customerIds?.length,
		v.siteIds?.length
	].filter(Boolean).length;

	const errorKeys = Object.keys(errors);
	const basicHasError = errorKeys.some(k => BASIC_FIELDS.has(k));
	const actionsHasError = errorKeys.includes('actions');
	const conditionsHasError = errorKeys.some(k => !BASIC_FIELDS.has(k) && k !== 'actions');

	const tabs: { key: EditorTab; icon: typeof Tag; label: string; count: number | null; error: boolean }[] = [
		{ key: 'basic', icon: Tag, label: t('promotion.basicInfo.tab'), count: null, error: basicHasError },
		{ key: 'conditions', icon: Filter, label: t('promotion.conditions.tab'), count: conditionsCount, error: conditionsHasError },
		{ key: 'actions', icon: Zap, label: t('promotion.action.tab'), count: v.actions?.length ?? 0, error: actionsHasError }
	];

	return (
		<div>
			<div className="mb-2 text-[10px] font-semibold tracking-[0.06em] text-foreground-subtle">{toGreekCaps(t('common.sections'))}</div>
			<div className="flex flex-col gap-1">
				{tabs.map(tab => {
					const active = tab.key === activeTab;
					const Icon = tab.icon;
					return (
						<button
							key={tab.key}
							type="button"
							onClick={() => onTabChange(tab.key)}
							className={cn(
								'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
								active
									? 'border-brand-border-subtle bg-brand-subtle text-brand-subtle-fg'
									: 'border-transparent text-foreground-muted hover:bg-muted'
							)}>
							<Icon size={16} />
							<span className="flex-1 text-left">{tab.label}</span>
							{tab.error && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
							{tab.count != null && tab.count > 0 && (
								<span
									className={cn(
										'rounded-pill px-1.5 text-[10px] font-medium',
										active ? 'bg-brand text-on-brand' : 'bg-muted text-foreground-subtle'
									)}>
									{tab.count}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}
